import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { CreditCard, Lock } from '@mui/icons-material';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import LandingLayout from './LandingLayout';
import ProgressBar from './ProgressBar';
import { getApiUrl } from '../utils/api';
import { getEnvStripePublishableKey, isValidStripePublishableKey } from '../utils/stripeConfig';
import { useAuth } from '../contexts/AuthContext';

/** Phone numbers that will receive SMS during signup (loved one + care circle). */
export function getSignupSmsRecipients(): Array<{ name: string; phone: string }> {
  const recipients: Array<{ name: string; phone: string }> = [];
  try {
    const customerInfo = JSON.parse(localStorage.getItem('signupCustomerInfo') || '{}');
    if (customerInfo.phoneNumber) {
      recipients.push({
        name: customerInfo.name || 'Loved One',
        phone: String(customerInfo.phoneNumber),
      });
    }
    const careCircleInfo = JSON.parse(localStorage.getItem('signupCareCircleInfo') || '{}');
    if (careCircleInfo.caregivers && Array.isArray(careCircleInfo.caregivers)) {
      careCircleInfo.caregivers.forEach((caregiver: any) => {
        if (caregiver.phoneNumber) {
          recipients.push({
            name: `${caregiver.firstName || ''} ${caregiver.lastName || ''}`.trim() || 'Caregiver',
            phone: String(caregiver.phoneNumber),
          });
        }
      });
    }
  } catch {
    // ignore parse errors — empty list is fine
  }
  return recipients;
}

interface PaymentFormData {
  cardholderName: string;
  billingAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

interface PlanResponse {
  id: number;
  name: string;
  stripe_price_id: string;
  interval: string;
  interval_count?: number;
  is_active: boolean;
}

const FALLBACK_MONTHLY_PRICE_ID =
  process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID || 'price_1TSDm92NwMoiEs0sv7vJKwcO';
const FALLBACK_ANNUAL_PRICE_ID =
  process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID || 'price_1TSDm92NwMoiEs0se5T0P8Gs';

const cardElementOptions = {
  // Stripe Link overlays (esp. Safari) steal clicks from nearby page controls.
  disableLink: true,
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#d32f2f',
    },
  },
};

const PaymentPageForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [paymentData, setPaymentData] = useState<PaymentFormData>({
    cardholderName: '',
    billingAddress: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const apiUrl = getApiUrl();
  const smsRecipients = getSignupSmsRecipients();

  useEffect(() => {
    const savedPaymentData = localStorage.getItem('signupPaymentData');
    if (savedPaymentData) {
      try {
        const data = JSON.parse(savedPaymentData);
        setPaymentData(data);
      } catch (err) {
        console.error('Error loading saved payment data:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (paymentData.cardholderName || paymentData.billingAddress) {
      localStorage.setItem('signupPaymentData', JSON.stringify(paymentData));
    }
  }, [paymentData]);

  const handleInputChange = (field: keyof PaymentFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }
  ) => {
    setPaymentData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleContinue = async () => {
    if (!stripe || !elements) {
      setError('Payment service is not ready. Please refresh and try again.');
      return;
    }

    if (!paymentData.cardholderName || !paymentData.billingAddress || !paymentData.city || !paymentData.state || !paymentData.zipCode) {
      setError('Please fill in all required billing information.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card input is not available. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setError('');
    let authToken: string | null = null;
    let createdCustomerId: number | null = null;

    try {
      const userInfo = JSON.parse(localStorage.getItem('signupUserInfo') || '{}');
      const customerInfo = JSON.parse(localStorage.getItem('signupCustomerInfo') || '{}');
      const careCircleInfo = JSON.parse(localStorage.getItem('signupCareCircleInfo') || '{}');
      const selectedBillingPlan = localStorage.getItem('selectedBillingPlan') || 'monthly';

      if (!userInfo.email || !userInfo.password) {
        throw new Error('Missing user information. Please start over.');
      }
      if (!customerInfo.name || !customerInfo.phoneNumber || !customerInfo.caregiverPhone) {
        throw new Error('Missing customer information. Please start over.');
      }
      if (!careCircleInfo.caregivers || careCircleInfo.caregivers.length === 0) {
        throw new Error('Missing care circle information. Please start over.');
      }

      const fullName = userInfo.firstName && userInfo.lastName
        ? `${userInfo.firstName} ${userInfo.lastName}`
        : userInfo.fullName || 'Unknown User';

      const userResponse = await fetch(`${apiUrl}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userInfo.email,
          password: userInfo.password,
          full_name: fullName,
          phone_number: userInfo.phoneNumber,
          timezone: userInfo.timezone,
        }),
      });

      if (!userResponse.ok) {
        const errorData = await userResponse.json();
        throw new Error(`Failed to create user: ${errorData.detail || 'Unknown error'}`);
      }

      const userData = await userResponse.json();
      const token = userData.access_token || userData.token;
      if (!token) {
        throw new Error('Failed to get authentication token from user registration');
      }
      // Persist session so dashboard/nav work after signup (replaces any stale token)
      login(token);
      authToken = token;

      const checkInTimes: string[] = [];
      if (customerInfo.checkInTime1 && customerInfo.checkInTime1.trim()) {
        checkInTimes.push(customerInfo.checkInTime1.trim());
      }
      if (customerInfo.checkInTime2 && customerInfo.checkInTime2.trim()) {
        checkInTimes.push(customerInfo.checkInTime2.trim());
      }

      const customerResponse = await fetch(`${apiUrl}/customers/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: customerInfo.name,
          phone_number: customerInfo.phoneNumber.replace(/[^\d+]/g, ''),
          caregiver_name: customerInfo.caregiverName,
          caregiver_phone: customerInfo.caregiverPhone.replace(/[^\d+]/g, ''),
          caregiver_email: customerInfo.caregiverEmail,
          custom_message: customerInfo.customMessage || null,
          timezone: customerInfo.timezone || 'America/Los_Angeles',
          check_in_times: checkInTimes,
        }),
      });

      if (!customerResponse.ok) {
        const errorData = await customerResponse.json();
        throw new Error(`Failed to create customer: ${JSON.stringify(errorData)}`);
      }

      const customerData = await customerResponse.json();
      createdCustomerId = customerData.id;

      const paymentMethodResult = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: paymentData.cardholderName,
          email: userInfo.email,
          address: {
            line1: paymentData.billingAddress,
            city: paymentData.city,
            state: paymentData.state,
            postal_code: paymentData.zipCode,
            country: 'US',
          },
        },
      });

      if (paymentMethodResult.error || !paymentMethodResult.paymentMethod?.id) {
        throw new Error(paymentMethodResult.error?.message || 'Failed to create payment method');
      }

      const paymentMethodId = paymentMethodResult.paymentMethod.id;

      const plansResponse = await fetch(`${apiUrl}/payments/plans`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!plansResponse.ok) {
        const errorData = await plansResponse.json();
        throw new Error(`Failed to load plans: ${errorData.detail || 'Unknown error'}`);
      }
      const plans: PlanResponse[] = await plansResponse.json();
      const normalizedBillingPlan = selectedBillingPlan.toLowerCase();
      const activePlans = plans.filter((plan) => plan.is_active);
      let selectedPlan: PlanResponse | undefined;

      if (normalizedBillingPlan === 'annual') {
        selectedPlan = activePlans.find((plan) => {
          const interval = (plan.interval || '').toLowerCase();
          const name = (plan.name || '').toLowerCase();
          return (
            interval === 'year' ||
            interval === 'annual' ||
            name.includes('annual') ||
            (interval === 'month' && plan.interval_count === 12)
          );
        });
      } else {
        selectedPlan = activePlans.find((plan) => {
          const interval = (plan.interval || '').toLowerCase();
          const name = (plan.name || '').toLowerCase();
          return interval === 'month' || interval === 'monthly' || name.includes('monthly');
        });
      }

      if (!selectedPlan) {
        const fallbackPriceId = normalizedBillingPlan === 'annual' ? FALLBACK_ANNUAL_PRICE_ID : FALLBACK_MONTHLY_PRICE_ID;
        if (!fallbackPriceId) {
          throw new Error(`No active ${normalizedBillingPlan} plan found.`);
        }
        console.warn(`No active ${normalizedBillingPlan} plan found in API response. Using fallback Stripe price ID.`);
        selectedPlan = {
          id: -1,
          name: normalizedBillingPlan === 'annual' ? 'Annual Plan' : 'Monthly Plan',
          stripe_price_id: fallbackPriceId,
          interval: normalizedBillingPlan === 'annual' ? 'year' : 'month',
          is_active: true,
        };
      }

      const subscriptionResponse = await fetch(`${apiUrl}/payments/subscriptions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerData.id,
          price_id: selectedPlan.stripe_price_id,
          payment_method_id: paymentMethodId,
          trial_days: 0,
        }),
      });

      if (!subscriptionResponse.ok) {
        const errorData = await subscriptionResponse.json();
        throw new Error(`Failed to create subscription: ${errorData.detail || 'Unknown error'}`);
      }

      const caregiversToCreate = careCircleInfo.caregivers.filter((caregiver: any, index: number) => {
        return !(index === 0 && caregiver.isPrimary);
      });

      for (const caregiver of caregiversToCreate) {
        const caregiverResponse = await fetch(
          `${apiUrl}/caregiver-circles/customers/${customerData.id}/caregivers`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              caregiver_name: `${caregiver.firstName} ${caregiver.lastName}`,
              caregiver_phone: caregiver.phoneNumber,
              caregiver_email: caregiver.email && caregiver.email.trim() ? caregiver.email.trim() : null,
              role: caregiver.role.toLowerCase(),
              is_primary: caregiver.isPrimary,
            }),
          }
        );

        if (!caregiverResponse.ok) {
          const errorData = await caregiverResponse.json();
          throw new Error(`Failed to create caregiver ${caregiver.alertOrder}: ${JSON.stringify(errorData)}`);
        }
      }

      localStorage.removeItem('signupUserInfo');
      localStorage.removeItem('signupCustomerInfo');
      localStorage.removeItem('signupCareCircleInfo');
      localStorage.removeItem('selectedBillingPlan');
      localStorage.removeItem('signupPaymentData');

      navigate('/signup/payment-success', {
        state: {
          planName: selectedPlan.name,
          planPrice,
        },
      });
    } catch (err) {
      // Compensating transaction: if payment flow fails after creating a user/customer,
      // hard delete the customer and abandon the incomplete user account so email/phone
      // can be reused on the next signup attempt.
      if (createdCustomerId && authToken) {
        try {
          await fetch(`${apiUrl}/customers/${createdCustomerId}?hard_delete=true`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (cleanupError) {
          console.warn('Failed to rollback created customer after payment failure:', cleanupError);
        }
      }
      if (authToken) {
        try {
          await fetch(`${apiUrl}/auth/incomplete-signup`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (cleanupError) {
          console.warn('Failed to abandon incomplete signup after payment failure:', cleanupError);
        }
      }
      console.error('Payment processing error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during payment processing');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    localStorage.setItem('signupPaymentData', JSON.stringify(paymentData));
    navigate('/signup/care-circle');
  };

  const selectedPlan = localStorage.getItem('selectedBillingPlan') || 'monthly';
  const planPrice = selectedPlan === 'annual' ? '$130/year' : '$14.99/month';

  return (
    <LandingLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <ProgressBar
          currentStep={5}
          totalSteps={5}
          stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
        />

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Complete Your Payment
          </Typography>
          <Typography variant="h6" color="text.secondary">
            You've selected the {selectedPlan === 'annual' ? 'Annual' : 'Monthly'} plan. Complete your payment to get started.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} md={8} sx={{ position: 'relative', zIndex: 0 }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2">
                    Payment Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cardholder Name"
                      value={paymentData.cardholderName}
                      onChange={handleInputChange('cardholderName')}
                      placeholder="John Doe"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Card Details
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid #d1d5db',
                        borderRadius: 1,
                        p: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 0,
                        // Keep Stripe iframes from painting/click-stealing outside this box
                        '& iframe': {
                          maxWidth: '100%',
                        },
                      }}
                    >
                      <CardElement options={cardElementOptions} />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Billing Address
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Billing Address"
                      value={paymentData.billingAddress}
                      onChange={handleInputChange('billingAddress')}
                      placeholder="123 Main Street"
                      required
                    />
                  </Grid>

                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="City"
                      value={paymentData.city}
                      onChange={handleInputChange('city')}
                      placeholder="San Francisco"
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth required>
                      <InputLabel>State</InputLabel>
                      <Select value={paymentData.state} onChange={handleInputChange('state')} label="State">
                        <MenuItem value="AL">Alabama</MenuItem>
                        <MenuItem value="AK">Alaska</MenuItem>
                        <MenuItem value="AZ">Arizona</MenuItem>
                        <MenuItem value="AR">Arkansas</MenuItem>
                        <MenuItem value="CA">California</MenuItem>
                        <MenuItem value="CO">Colorado</MenuItem>
                        <MenuItem value="CT">Connecticut</MenuItem>
                        <MenuItem value="DE">Delaware</MenuItem>
                        <MenuItem value="DC">District of Columbia</MenuItem>
                        <MenuItem value="FL">Florida</MenuItem>
                        <MenuItem value="GA">Georgia</MenuItem>
                        <MenuItem value="HI">Hawaii</MenuItem>
                        <MenuItem value="ID">Idaho</MenuItem>
                        <MenuItem value="IL">Illinois</MenuItem>
                        <MenuItem value="IN">Indiana</MenuItem>
                        <MenuItem value="IA">Iowa</MenuItem>
                        <MenuItem value="KS">Kansas</MenuItem>
                        <MenuItem value="KY">Kentucky</MenuItem>
                        <MenuItem value="LA">Louisiana</MenuItem>
                        <MenuItem value="ME">Maine</MenuItem>
                        <MenuItem value="MD">Maryland</MenuItem>
                        <MenuItem value="MA">Massachusetts</MenuItem>
                        <MenuItem value="MI">Michigan</MenuItem>
                        <MenuItem value="MN">Minnesota</MenuItem>
                        <MenuItem value="MS">Mississippi</MenuItem>
                        <MenuItem value="MO">Missouri</MenuItem>
                        <MenuItem value="MT">Montana</MenuItem>
                        <MenuItem value="NE">Nebraska</MenuItem>
                        <MenuItem value="NV">Nevada</MenuItem>
                        <MenuItem value="NH">New Hampshire</MenuItem>
                        <MenuItem value="NJ">New Jersey</MenuItem>
                        <MenuItem value="NM">New Mexico</MenuItem>
                        <MenuItem value="NY">New York</MenuItem>
                        <MenuItem value="NC">North Carolina</MenuItem>
                        <MenuItem value="ND">North Dakota</MenuItem>
                        <MenuItem value="OH">Ohio</MenuItem>
                        <MenuItem value="OK">Oklahoma</MenuItem>
                        <MenuItem value="OR">Oregon</MenuItem>
                        <MenuItem value="PA">Pennsylvania</MenuItem>
                        <MenuItem value="RI">Rhode Island</MenuItem>
                        <MenuItem value="SC">South Carolina</MenuItem>
                        <MenuItem value="SD">South Dakota</MenuItem>
                        <MenuItem value="TN">Tennessee</MenuItem>
                        <MenuItem value="TX">Texas</MenuItem>
                        <MenuItem value="UT">Utah</MenuItem>
                        <MenuItem value="VT">Vermont</MenuItem>
                        <MenuItem value="VA">Virginia</MenuItem>
                        <MenuItem value="WA">Washington</MenuItem>
                        <MenuItem value="WV">West Virginia</MenuItem>
                        <MenuItem value="WI">Wisconsin</MenuItem>
                        <MenuItem value="WY">Wyoming</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={paymentData.zipCode}
                      onChange={handleInputChange('zipCode')}
                      placeholder="94105"
                      inputProps={{ maxLength: 10 }}
                      required
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Lock sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Card details are handled securely by Stripe.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{ position: 'relative', zIndex: 20, isolation: 'isolate' }}
          >
            <Card sx={{ position: 'relative', zIndex: 20 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  Order Summary
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">{selectedPlan === 'annual' ? 'Annual Plan' : 'Monthly Plan'}</Typography>
                    <Typography variant="body1" fontWeight="bold">{planPrice}</Typography>
                  </Box>

                  {selectedPlan === 'annual' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Savings</Typography>
                      <Typography variant="body2" color="success.main" fontWeight="bold">-$49.88</Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6" color="primary.main" fontWeight="bold">{planPrice}</Typography>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Secure payment processing powered by Stripe
                </Typography>

                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    SMS Consent
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    By Clicking Complete Payment I agree that these numbers will receive wellness-check
                    text messages:
                  </Typography>
                  <List dense sx={{ py: 0, mb: 1 }}>
                    {smsRecipients.map((recipient, index) => (
                      <ListItem key={`${recipient.phone}-${index}`} sx={{ py: 0.25, px: 0 }}>
                        <ListItemText
                          primary={recipient.name}
                          secondary={recipient.phone}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                    {smsRecipients.length === 0 && (
                      <ListItem sx={{ py: 0.25, px: 0 }}>
                        <ListItemText primary="No phone numbers found — go back and check Care Circle details." />
                      </ListItem>
                    )}
                  </List>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Message and data rates may apply. You can opt out anytime by contacting support.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={handleBack} disabled={loading} sx={{ flex: 1 }}>
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    {loading ? 'Processing...' : 'Complete Payment'}
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </LandingLayout>
  );
};

const PaymentPage: React.FC = () => {
  const envKey = getEnvStripePublishableKey();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(() =>
    isValidStripePublishableKey(envKey) ? loadStripe(envKey) : null
  );
  const [status, setStatus] = useState<'ready' | 'loading' | 'error'>(
    isValidStripePublishableKey(envKey) ? 'ready' : 'loading'
  );

  useEffect(() => {
    if (stripePromise) {
      return;
    }

    let cancelled = false;
    const loadConfig = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/payments/public-config`);
        if (!response.ok) {
          throw new Error('config unavailable');
        }
        const data = await response.clone().json();
        const key = data?.stripe_publishable_key || '';
        if (!isValidStripePublishableKey(key)) {
          throw new Error('invalid key');
        }
        if (!cancelled) {
          setStripePromise(loadStripe(key));
          setStatus('ready');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    };

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [stripePromise]);

  if (status === 'loading') {
    return (
      <LandingLayout>
        <Container maxWidth="sm" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress aria-label="Loading payment form" />
        </Container>
      </LandingLayout>
    );
  }

  if (status === 'error' || !stripePromise) {
    return (
      <LandingLayout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="error">
            Payments are temporarily unavailable. Please try again in a few minutes, or contact support.
          </Alert>
        </Container>
      </LandingLayout>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentPageForm />
    </Elements>
  );
};

export default PaymentPage;
